import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Divider,
  useToast,
  Input,
  Select,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { formatDate } from "utils/format";
import referralsService from "services/referralsService";

const PAGE_SIZE = 20;

export default function IReferalsOverview() {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const badgeBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.100");
  const rowHoverBg = useColorModeValue("secondaryGray.50", "whiteAlpha.50");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsByReferral, setStudentsByReferral] = useState([]);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [referralToDelete, setReferralToDelete] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [formData, setFormData] = useState({
    sn: "",
    name: "",
    email: "",
    phone_number: "",
    referral_code: "",
    processing_status: "unprocessed",
  });

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose,
  } = useDisclosure();
  const {
    isOpen: isStudentsOpen,
    onOpen: onStudentsOpen,
    onClose: onStudentsClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const fileInputRef = useRef(null);
  const cancelDeleteRef = useRef(null);
  const toast = useToast();

  const fetchReferrals = async (nextSkip = 0) => {
    setLoading(true);
    setError(null);
    try {
      const result = await referralsService.getReferrals({
        skip: nextSkip,
        limit: PAGE_SIZE,
        search: search || undefined,
        status_filter: statusFilter || undefined,
      });

      if (result.success && result.data) {
        setReferrals(result.data.referrals || []);
        setTotal(result.data.total || 0);
        setSkip(result.data.skip || 0);
      } else {
        setError(result.error || "Failed to load referrals");
      }
    } catch (err) {
      console.error("Error loading referrals:", err);
      setError("Failed to load referrals. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleApplySearch = () => {
    fetchReferrals(0);
  };

  const resetForm = () => {
    setFormData({
      sn: "",
      name: "",
      email: "",
      phone_number: "",
      referral_code: "",
      processing_status: "unprocessed",
    });
    setFormErrors({});
  };

  const handleOpenCreate = () => {
    setIsEdit(false);
    resetForm();
    onFormOpen();
  };

  const handleOpenEdit = async (referral) => {
    setIsEdit(true);
    setLoadingForm(true);
    setFormErrors({});
    setSelectedReferral(referral);
    onFormOpen();

    try {
      // Fetch the complete referral details
      const result = await referralsService.getReferral(referral.id);
      
      if (result.success && result.data) {
        // Populate form with fetched data
        setFormData({
          sn: result.data.sn ?? "",
          name: result.data.name || "",
          email: result.data.email || "",
          phone_number: result.data.phone_number || "",
          referral_code: result.data.referral_code || "",
          processing_status: result.data.processing_status || "unprocessed",
        });
      } else {
        // Fallback to row data if fetch fails
        setFormData({
          sn: referral.sn ?? "",
          name: referral.name || "",
          email: referral.email || "",
          phone_number: referral.phone_number || "",
          referral_code: referral.referral_code || "",
          processing_status: referral.processing_status || "unprocessed",
        });
        toast({
          title: "Warning",
          description: "Could not fetch full details. Using cached data.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error loading referral details:", err);
      // Fallback to row data
      setFormData({
        sn: referral.sn ?? "",
        name: referral.name || "",
        email: referral.email || "",
        phone_number: referral.phone_number || "",
        referral_code: referral.referral_code || "",
        processing_status: referral.processing_status || "unprocessed",
      });
      toast({
        title: "Error",
        description: "Failed to load full referral details.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingForm(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!formData.phone_number.trim()) errors.phone_number = "Phone number is required";
    if (!formData.referral_code.trim()) errors.referral_code = "Referral code is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitForm = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const payload = {
        sn: formData.sn || undefined,
        name: formData.name,
        email: formData.email,
        phone_number: formData.phone_number,
        referral_code: formData.referral_code,
        processing_status: formData.processing_status,
      };

      const result = isEdit && selectedReferral
        ? await referralsService.updateReferral(selectedReferral.id, payload)
        : await referralsService.createReferral(payload);

      if (result.success) {
        toast({
          title: "Success",
          description: result.message || (isEdit ? "Referral updated." : "Referral created."),
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onFormClose();
        setSelectedReferral(null);
        resetForm();
        fetchReferrals(skip);
      } else {
        toast({
          title: "Error",
          description: result.error || "Request failed",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error saving referral:", err);
      toast({
        title: "Error",
        description: "Failed to save referral.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenStudents = async (referral) => {
    setSelectedReferral(referral);
    setStudentsByReferral([]);
    setStudentsLoading(true);
    onStudentsOpen();

    try {
      const result = await referralsService.getStudentsByReferral(referral.id, { skip: 0, limit: 50 });
      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : result.data?.students || [];
        setStudentsByReferral(list);
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to load students.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error loading referral students:", err);
      toast({
        title: "Error",
        description: "Failed to load referral students.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setStudentsLoading(false);
    }
  };

  const handleRequestDelete = (referral) => {
    setReferralToDelete(referral);
    onDeleteOpen();
  };

  const handleConfirmDelete = async () => {
    if (!referralToDelete) return;
    setDeleting(true);
    try {
      const result = await referralsService.deleteReferral(referralToDelete.id);
      if (result.success) {
        toast({
          title: "Deleted",
          description: result.message || "Referral deleted.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onDeleteClose();
        setReferralToDelete(null);
        fetchReferrals(skip);
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to delete referral.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error deleting referral:", err);
      toast({
        title: "Error",
        description: "Failed to delete referral.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const result = await referralsService.exportReferralsPdf({
        status_filter: statusFilter || undefined,
        search: search || undefined,
      });

      if (result.success) {
        toast({
          title: "Success",
          description: result.message || "Referrals PDF exported successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Export failed",
          description: result.error || "Failed to export PDF.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setExportingPdf(false);
    }
  };

  const canGoPrev = skip > 0;
  const canGoNext = skip + PAGE_SIZE < total;

  const handlePickCsv = () => {
    fileInputRef.current?.click();
  };

  const handleCsvSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast({
        title: "Invalid file",
        description: "Please select a CSV file.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      event.target.value = "";
      return;
    }

    setUploadingCsv(true);
    try {
      const result = await referralsService.uploadReferralsCsv(file);
      if (result.success) {
        const summary = result.data
          ? `Rows: ${result.data.total_rows ?? 0}, Queued: ${result.data.queued ?? 0}, Skipped duplicates: ${result.data.skipped_duplicates ?? 0}`
          : "CSV uploaded successfully.";

        toast({
          title: "Import successful",
          description: summary,
          status: "success",
          duration: 6000,
          isClosable: true,
        });
        await fetchReferrals(0);
      } else {
        toast({
          title: "Import failed",
          description: result.error || "Failed to upload CSV.",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error uploading CSV:", err);
      toast({
        title: "Import failed",
        description: "Failed to upload CSV. Please try again.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setUploadingCsv(false);
      event.target.value = "";
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card p="30px" bg={cardBg}>
        <Flex align="center" justify="space-between" mb="20px" flexWrap="wrap" gap="10px">
          <Text color={textColor} fontSize="2xl" fontWeight="700">
            Referals Overview
          </Text>
          <Flex align="center" gap="10px" flexWrap="wrap">
            <Text color={textColorSecondary} fontSize="sm">
              Total: {total}
            </Text>
            <Button size="sm" colorScheme="green" onClick={handleOpenCreate}>
              New Referral
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={handlePickCsv}
              isLoading={uploadingCsv}
              loadingText="Importing"
            >
              Import CSV
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="purple"
              onClick={handleExportPdf}
              isLoading={exportingPdf}
              loadingText="Exporting"
            >
              Export PDF
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleCsvSelected}
            />
          </Flex>
        </Flex>
        <Flex gap="10px" mb="16px" flexWrap="wrap">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, referral code"
            maxW="360px"
          />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            maxW="220px"
            placeholder="All statuses"
          >
            <option value="pending">Pending</option>
            <option value="processed">Processed</option>
            <option value="failed">Failed</option>
            <option value="unprocessed">Unprocessed</option>
          </Select>
          <Button size="sm" onClick={handleApplySearch} colorScheme="brand">
            Apply
          </Button>
        </Flex>
        <Divider borderColor={borderColor} mb="20px" />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minH="220px">
            <Spinner size="lg" color="brand.500" />
          </Box>
        ) : error ? (
          <Alert status="error" borderRadius="12px">
            <AlertIcon />
            <Text color={textColor}>{error}</Text>
          </Alert>
        ) : (
          <>
            <Box overflowX="auto">
              <Table variant="simple" color="secondaryGray.700">
                <Thead>
                  <Tr>
                    <Th color={textColorSecondary}>SN</Th>
                    <Th color={textColorSecondary}>Name</Th>
                    <Th color={textColorSecondary}>Email</Th>
                    <Th color={textColorSecondary}>Phone</Th>
                    <Th color={textColorSecondary}>Referral Code</Th>
                    <Th color={textColorSecondary}>Status</Th>
                    <Th color={textColorSecondary}>Processed At</Th>
                    <Th color={textColorSecondary}>Created At</Th>
                    <Th color={textColorSecondary}>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {referrals.length > 0 ? (
                    referrals.map((referral) => (
                      <Tr key={referral.id} _hover={{ bg: rowHoverBg }}>
                        <Td><Text color={textColor} fontSize="sm">{referral.sn ?? "-"}</Text></Td>
                        <Td><Text color={textColor} fontSize="sm" fontWeight="600">{referral.name || "-"}</Text></Td>
                        <Td><Text color={textColor} fontSize="sm">{referral.email || "-"}</Text></Td>
                        <Td><Text color={textColor} fontSize="sm">{referral.phone_number || "-"}</Text></Td>
                        <Td><Text color={textColor} fontSize="sm" fontFamily="mono">{referral.referral_code || "-"}</Text></Td>
                        <Td>
                          <Badge
                            colorScheme={
                              referral.processing_status === "failed"
                                ? "red"
                                : referral.processing_status === "unprocessed"
                                ? "orange"
                                : "green"
                            }
                            bg={badgeBg}
                            color={textColor}
                            fontSize="xs"
                            fontWeight="500"
                            px="8px"
                            py="4px"
                            borderRadius="8px"
                          >
                            {(referral.processing_status || "-").toUpperCase()}
                          </Badge>
                        </Td>
                        <Td>
                          <Text color={textColor} fontSize="sm">
                            {referral.processed_at ? formatDate(referral.processed_at) : "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Text color={textColor} fontSize="sm">
                            {referral.created_at ? formatDate(referral.created_at) : "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Flex gap="6px" flexWrap="wrap">
                            <Button size="xs" variant="outline" onClick={() => handleOpenEdit(referral)}>
                              Edit
                            </Button>
                            <Button size="xs" variant="outline" colorScheme="purple" onClick={() => handleOpenStudents(referral)}>
                              Students
                            </Button>
                            <Button size="xs" variant="outline" colorScheme="red" onClick={() => handleRequestDelete(referral)}>
                              Delete
                            </Button>
                          </Flex>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={9} textAlign="center" py="20px">
                        <Text color={textColorSecondary} fontSize="sm">
                          No referrals found
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>

            <Flex justify="space-between" align="center" mt="20px">
              <Text color={textColorSecondary} fontSize="xs">
                Showing {Math.min(skip + 1, total)}-{Math.min(skip + PAGE_SIZE, total)} of {total}
              </Text>
              <Flex gap="10px">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchReferrals(Math.max(0, skip - PAGE_SIZE))}
                  isDisabled={!canGoPrev}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => fetchReferrals(skip + PAGE_SIZE)}
                  isDisabled={!canGoNext}
                >
                  Next
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      </Card>

      <Modal isOpen={isFormOpen} onClose={onFormClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEdit ? "Edit Referral" : "New Referral"}</ModalHeader>
          <ModalCloseButton isDisabled={loadingForm} />
          <ModalBody>
            {loadingForm ? (
              <Box display="flex" justifyContent="center" alignItems="center" minH="300px">
                <Spinner size="lg" color="brand.500" />
              </Box>
            ) : (
              <Flex direction="column" gap="12px">
                <FormControl>
                  <FormLabel>SN</FormLabel>
                  <Input value={formData.sn} onChange={(e) => setFormData((prev) => ({ ...prev, sn: e.target.value }))} />
                </FormControl>
                <FormControl isRequired isInvalid={!!formErrors.name}>
                  <FormLabel>Name</FormLabel>
                  <Input value={formData.name} onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))} />
                </FormControl>
                <FormControl isRequired isInvalid={!!formErrors.email}>
                  <FormLabel>Email</FormLabel>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))} />
                </FormControl>
                <FormControl isRequired isInvalid={!!formErrors.phone_number}>
                  <FormLabel>Phone Number</FormLabel>
                  <Input value={formData.phone_number} onChange={(e) => setFormData((prev) => ({ ...prev, phone_number: e.target.value }))} />
                </FormControl>
                <FormControl isRequired isInvalid={!!formErrors.referral_code}>
                  <FormLabel>Referral Code</FormLabel>
                  <Input value={formData.referral_code} onChange={(e) => setFormData((prev) => ({ ...prev, referral_code: e.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={formData.processing_status}
                    onChange={(e) => setFormData((prev) => ({ ...prev, processing_status: e.target.value }))}
                  >
                    <option value="pending">pending</option>
                    <option value="processed">processed</option>
                    <option value="failed">failed</option>
                    <option value="unprocessed">unprocessed</option>
                  </Select>
                </FormControl>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose} isDisabled={submitting || loadingForm}>
              Cancel
            </Button>
            <Button colorScheme="brand" onClick={handleSubmitForm} isLoading={submitting} isDisabled={loadingForm}>
              {isEdit ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isStudentsOpen} onClose={onStudentsClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Referral Students {selectedReferral?.name ? `- ${selectedReferral.name}` : ""}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {studentsLoading ? (
              <Box py="12" textAlign="center">
                <Spinner size="md" />
              </Box>
            ) : studentsByReferral.length > 0 ? (
              <Box overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Phone</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {studentsByReferral.map((student, idx) => (
                      <Tr key={student.id || idx}>
                        <Td>{student.name || student.full_name || "-"}</Td>
                        <Td>{student.email || "-"}</Td>
                        <Td>{student.phone_number || student.phone || "-"}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Text color={textColorSecondary}>No students found for this referral.</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onStudentsClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete Referral</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete {referralToDelete?.name || "this referral"}?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3} isLoading={deleting}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
